/** Shared URL + email field helpers (web + validation). Mirrors labwick-app `urlFieldValue`. */

const EMAIL_REGEX = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

export function isUrlFieldType(type: string | undefined): boolean {
  return (type || "").toLowerCase() === "url";
}

export function isEmailFieldType(type: string | undefined): boolean {
  return (type || "").toLowerCase() === "email";
}

export function isLinkFieldType(type: string | undefined): boolean {
  return isUrlFieldType(type) || isEmailFieldType(type);
}

export function coerceLinkFieldValueForSave(raw: unknown): string {
  if (raw == null) return "";
  return String(raw).trim();
}

export function normalizeUrlForHref(value: string): string | null {
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  if (/^\/\//.test(trimmed)) return `https:${trimmed}`;
  return `https://${trimmed}`;
}

export function externalHrefForLinkField(
  fieldType: string | undefined,
  raw: unknown
): string | null {
  const t = (fieldType || "").toLowerCase();
  if (raw == null) return null;
  const trimmed = String(raw).trim();
  if (!trimmed) return null;
  if (t === "email") return `mailto:${trimmed}`;
  if (t === "url") return normalizeUrlForHref(trimmed);
  return null;
}

export function isValidEmailValue(value: string): boolean {
  const t = value.trim();
  if (!t) return true;
  return EMAIL_REGEX.test(t);
}

export function isValidUrlValue(value: string): boolean {
  const t = value.trim();
  if (!t) return true;
  if (/^[a-z][a-z0-9+.-]*:/i.test(t) && !/^https?:\/\//i.test(t)) {
    return false;
  }
  const href = normalizeUrlForHref(t);
  if (!href) return false;
  try {
    const u = new URL(href);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

export type LinkFieldValidationOptions = {
  required?: boolean;
};

/**
 * Returns a user-facing error string, or null when valid.
 * Validates **both** `url` and `email` field types.
 */
export function validateLinkFieldValue(
  fieldType: string | undefined,
  raw: unknown,
  options: LinkFieldValidationOptions = {}
): string | null {
  const t = (fieldType || "").toLowerCase();
  if (!isLinkFieldType(t)) return null;

  const s = raw == null ? "" : String(raw).trim();
  if (!s) {
    if (options.required) return "This field is required.";
    return null;
  }
  if (t === "email" && !isValidEmailValue(s)) {
    return "Enter a valid email address.";
  }
  if (t === "url" && !isValidUrlValue(s)) {
    return "Enter a valid URL (e.g. https://example.com).";
  }
  return null;
}

export function linkFieldInputHtmlProps(fieldType: string | undefined): {
  type?: "email" | "url" | "text";
  autoComplete?: string;
  inputMode?: "email" | "url" | "text";
} {
  const t = (fieldType || "").toLowerCase();
  if (t === "email") {
    return { type: "email", autoComplete: "email", inputMode: "email" };
  }
  if (t === "url") {
    return { type: "url", autoComplete: "url", inputMode: "url" };
  }
  return { type: "text" };
}

/** Call before item create/update/public submit. Returns first error or null. */
export function findFirstLinkFieldValidationError(
  fields: Array<{ name?: string; type?: string; required?: boolean }>,
  values: Record<string, unknown>
): string | null {
  for (const field of fields) {
    const name = field.name?.trim();
    if (!name) continue;
    const err = validateLinkFieldValue(field.type, values[name], {
      required: field.required === true,
    });
    if (err) return err;
  }
  return null;
}

/** Validates link fields and returns a copy with trimmed string values. */
export function normalizeItemLinkFieldsForSave<T extends Record<string, unknown>>(
  data: T,
  fields: Array<{ name?: string; type?: string; required?: boolean }> | undefined
): { data: T; error: string | null } {
  const linkErr = findFirstLinkFieldValidationError(fields || [], data);
  if (linkErr) {
    return { data, error: linkErr };
  }
  if (!fields?.length) {
    return { data, error: null };
  }
  const out = { ...data };
  for (const field of fields) {
    const name = field.name?.trim();
    if (!name || !isLinkFieldType(field.type)) continue;
    (out as Record<string, unknown>)[name] = coerceLinkFieldValueForSave(
      out[name as keyof T]
    );
  }
  return { data: out, error: null };
}
